// Firebase-only API with real-time sync
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  runTransaction,
} from 'firebase/firestore';
import { db } from './firebase';

// Collection names
const COLLECTIONS = {
  events: 'bizops_events',
  inventory: 'bizops_inventory',
  talent: 'bizops_talent',
  castings: 'bizops_castings',
  invoices: 'bizops_invoices',
  customers: 'bizops_customers',
  models: 'bizops_models',
  tasks: 'bizops_tasks',
  staff: 'bizops_staff',
  roles: 'bizops_roles',
  departments: 'bizops_departments',
  refunds: 'bizops_refunds',
  refundPrices: 'bizops_refund_prices',
  refundBranches: 'bizops_refund_branches',
  refundBanks: 'bizops_refund_banks',
  inventoryCategories: 'bizops_inventory_categories',
  logistic: 'bizops_logistic',
  expenses: 'bizops_expenses',
  customerPayments: 'bizops_customer_payments',
  staffLineIds: 'bizops_staff_line_ids',
  projects: 'bizops_projects',
  finance: 'bizops_finance',
  parties: 'bizops_parties',
  partyCounters: 'bizops_party_counters',
  venues: 'bizops_venues',
  salesDocs: 'bizops_sales_docs',
  chatGuide: 'bizops_chat_guide',
};

// Helper to convert Firestore timestamps (returns new object, does not mutate)
const convertTimestamp = (data) => ({
  ...data,
  created_at: data.created_at?.toDate ? data.created_at.toDate().toISOString() : data.created_at,
  updated_at: data.updated_at?.toDate ? data.updated_at.toDate().toISOString() : data.updated_at,
});

// Generic Firestore service
const createService = (collectionName) => ({
  // Get all documents
  getAll: async () => {
    const colRef = collection(db, COLLECTIONS[collectionName]);
    const snapshot = await getDocs(colRef);
    const data = snapshot.docs.map((doc) => convertTimestamp({
      id: doc.id,
      ...doc.data(),
    }));
    return { data };
  },

  // Create document
  create: async (data) => {
    const colRef = collection(db, COLLECTIONS[collectionName]);
    const docData = {
      ...data,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    };
    const docRef = await addDoc(colRef, docData);
    return { data: { id: docRef.id, ...data } };
  },

  // Update document
  update: async (id, data) => {
    const docRef = doc(db, COLLECTIONS[collectionName], id);
    const updateData = {
      ...data,
      updated_at: serverTimestamp(),
    };
    await updateDoc(docRef, updateData);
    return { data: { id, ...data } };
  },

  // Delete document
  delete: async (id) => {
    const docRef = doc(db, COLLECTIONS[collectionName], id);
    await deleteDoc(docRef);
    return { data: { message: 'Deleted' } };
  },

  // Real-time subscription
  subscribe: (callback, onError) => {
    const colRef = collection(db, COLLECTIONS[collectionName]);
    return onSnapshot(
      colRef,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => convertTimestamp({ id: doc.id, ...doc.data() }));
        callback(data);
      },
      (err) => {
        console.error(`[${collectionName}] snapshot error:`, err);
        onError?.(err);
      }
    );
  },
});

// Events API with upcoming filter
export const eventsApi = {
  ...createService('events'),
  getUpcoming: async () => {
    const today = new Date().toISOString().split('T')[0];
    const colRef = collection(db, COLLECTIONS.events);
    const q = query(colRef, where('date', '>=', today), orderBy('date', 'asc'));
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map((doc) => convertTimestamp({
      id: doc.id,
      ...doc.data(),
    }));
    return { data };
  },
  subscribeUpcoming: (callback) => {
    const today = new Date().toISOString().split('T')[0];
    const colRef = collection(db, COLLECTIONS.events);
    const q = query(colRef, where('date', '>=', today), orderBy('date', 'asc'));
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => convertTimestamp({
        id: doc.id,
        ...doc.data(),
      }));
      callback(data);
    });
  },
};

// Other module APIs
export const inventoryApi = createService('inventory');
export const talentApi = createService('talent');
export const castingsApi = createService('castings');
export const invoicesApi = createService('invoices');
export const customersApi = createService('customers');
export const modelsApi = createService('models');
export const tasksApi = createService('tasks');
export const staffApi = createService('staff');
export const rolesApi = createService('roles');
export const departmentsApi = createService('departments');
export const refundsApi = createService('refunds');
export const refundPricesApi = createService('refundPrices');
export const refundBranchesApi = createService('refundBranches');
export const refundBanksApi = createService('refundBanks');
export const inventoryCategoriesApi = createService('inventoryCategories');
export const logisticApi = createService('logistic');
export const projectsApi = createService('projects');
export const venuesApi = createService('venues');
export const salesDocsApi = createService('salesDocs');
export const chatGuideApi = createService('chatGuide');

// ─── Parties (customers / vendors master) ───────────────────────────────────
// A single central register both Billing and CustomerPayments reference by id,
// so a customer's tax info is entered once and their history rolls up per party.
// `code` (P-0001…) is assigned atomically from a counter doc, like doc numbers.
export const partiesApi = {
  ...createService('parties'),
  // Atomic running code — returns e.g. "P-0007"
  getNextCode: async () => {
    const ctrRef = doc(db, COLLECTIONS.partyCounters, 'party');
    let next;
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ctrRef);
      next = (snap.exists() ? snap.data().last_number : 0) + 1;
      tx.set(ctrRef, { last_number: next }, { merge: true });
    });
    return `P-${String(next).padStart(4, '0')}`;
  },
  // Create with an auto-assigned code
  createWithCode: async (data) => {
    const code = await partiesApi.getNextCode();
    return partiesApi.create({ ...data, code });
  },
};

// Finance is stored as ONE singleton document (fixed id) so imported data is
// stable across sessions and deploys — no auto-id duplicates, no "load the
// first doc" ambiguity. Legacy auto-id docs are migrated into it on first load.
const FINANCE_DOC_ID = 'current';
export const financeApi = {
  load: async () => {
    const ref = doc(db, COLLECTIONS.finance, FINANCE_DOC_ID);
    const snap = await getDoc(ref);
    if (snap.exists()) return { id: snap.id, ...snap.data() };
    // Migrate any pre-existing auto-id document into the singleton
    const legacy = await getDocs(collection(db, COLLECTIONS.finance));
    const first = legacy.docs.find((d) => d.id !== FINANCE_DOC_ID);
    if (first) {
      const data = first.data();
      await setDoc(ref, { ...data, updated_at: serverTimestamp() });
      return { id: FINANCE_DOC_ID, ...data };
    }
    return null;
  },
  save: async (data) => {
    const ref = doc(db, COLLECTIONS.finance, FINANCE_DOC_ID);
    await setDoc(ref, { ...data, updated_at: serverTimestamp() }, { merge: true });
    return { id: FINANCE_DOC_ID };
  },
  clear: async () => {
    await deleteDoc(doc(db, COLLECTIONS.finance, FINANCE_DOC_ID));
  },
};

// Expenses API with month-filter helper
export const expensesApi = {
  ...createService('expenses'),

  getByMonth: async (year, month) => {
    const start = new Date(year, month - 1, 1).toISOString().split('T')[0];
    const end = new Date(year, month, 0).toISOString().split('T')[0];
    const colRef = collection(db, COLLECTIONS.expenses);
    const q = query(
      colRef,
      where('slip_date', '>=', start),
      where('slip_date', '<=', end),
      orderBy('slip_date', 'desc')
    );
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map((d) => convertTimestamp({ id: d.id, ...d.data() }));
    return { data };
  },

  subscribeAll: (callback) => {
    const colRef = collection(db, COLLECTIONS.expenses);
    const q = query(colRef, orderBy('created_at', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => convertTimestamp({ id: d.id, ...d.data() }));
      callback(data);
    });
  },

  confirm: async (id) => {
    const docRef = doc(db, COLLECTIONS.expenses, id);
    await updateDoc(docRef, {
      status: 'confirmed',
      confirmed_at: new Date().toISOString(),
      updated_at: serverTimestamp(),
    });
  },

  reject: async (id) => {
    const docRef = doc(db, COLLECTIONS.expenses, id);
    await updateDoc(docRef, {
      status: 'rejected',
      updated_at: serverTimestamp(),
    });
  },
};

// Customer Payments API
export const customerPaymentsApi = {
  ...createService('customerPayments'),

  subscribeAll: (callback) => {
    const colRef = collection(db, COLLECTIONS.customerPayments);
    const q = query(colRef, orderBy('created_at', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => convertTimestamp({ id: d.id, ...d.data() }));
      callback(data);
    });
  },

  confirm: async (id, paymentType) => {
    const docRef = doc(db, COLLECTIONS.customerPayments, id);
    await updateDoc(docRef, {
      status: 'confirmed',
      payment_type: paymentType,
      confirmed_at: new Date().toISOString(),
      updated_at: serverTimestamp(),
    });
  },

  reject: async (id) => {
    const docRef = doc(db, COLLECTIONS.customerPayments, id);
    await updateDoc(docRef, { status: 'rejected', updated_at: serverTimestamp() });
  },
};

// Staff Line IDs API (admin manages who is staff)
export const staffLineIdsApi = {
  ...createService('staffLineIds'),

  subscribeAll: (callback) => {
    const colRef = collection(db, COLLECTIONS.staffLineIds);
    return onSnapshot(colRef, (snapshot) => {
      const data = snapshot.docs.map((d) => convertTimestamp({ id: d.id, ...d.data() }));
      callback(data);
    });
  },
};

// Analytics API (calculates from Firestore data)
export const analyticsApi = {
  getStats: async () => {
    const [events, inventory, talent, castings, invoices, customers] = await Promise.all([
      eventsApi.getAll(),
      inventoryApi.getAll(),
      talentApi.getAll(),
      castingsApi.getAll(),
      invoicesApi.getAll(),
      customersApi.getAll(),
    ]);

    const totalRevenue = invoices.data
      .filter((i) => i.status === 'paid')
      .reduce((acc, i) => acc + (i.amount || 0), 0);
    const pendingInvoices = invoices.data.filter((i) => i.status === 'pending').length;
    const activeProjects = castings.data.filter((c) =>
      c.status === 'in_progress' || c.status === 'pending'
    ).length;

    return {
      data: {
        total_events: events.data.length,
        total_inventory: inventory.data.length,
        total_talent: talent.data.length,
        total_castings: castings.data.length,
        total_customers: customers.data.length,
        total_revenue: totalRevenue,
        pending_invoices: pendingInvoices,
        active_projects: activeProjects,
      },
    };
  },

  // Subscribe to stats in real-time — reads data from snapshots directly, no extra getDocs
  subscribeStats: (callback) => {
    const cache = { events: [], inventory: [], talent: [], castings: [], invoices: [], customers: [] };

    const emit = () => {
      const totalRevenue = cache.invoices
        .filter((i) => i.status === 'paid')
        .reduce((acc, i) => acc + (i.amount || 0), 0);
      callback({
        total_events: cache.events.length,
        total_inventory: cache.inventory.length,
        total_talent: cache.talent.length,
        total_castings: cache.castings.length,
        total_customers: cache.customers.length,
        total_revenue: totalRevenue,
        pending_invoices: cache.invoices.filter((i) => i.status === 'pending').length,
        active_projects: cache.castings.filter((c) => c.status === 'in_progress' || c.status === 'pending').length,
      });
    };

    const watch = (key) => {
      const colRef = collection(db, COLLECTIONS[key]);
      return onSnapshot(
        colRef,
        (snap) => { cache[key] = snap.docs.map((d) => ({ id: d.id, ...d.data() })); emit(); },
        (err) => console.error(`[stats/${key}] snapshot error:`, err)
      );
    };

    const unsubs = ['events', 'inventory', 'talent', 'castings', 'invoices', 'customers'].map(watch);
    return () => unsubs.forEach((u) => u());
  },
};
